import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import MicIcon from '@material-ui/icons/Mic'
import StopIcon from '@material-ui/icons/Stop'
import { useIntl, FormattedMessage } from 'react-intl'
import useAudioPlayer from './hooks/useAudioPlayer'
import useAzureSpeechRecognition from './hooks/useAzureSpeechRecognition'
import { CapeeshColors } from '../../../../assets/ColorPalette'
import renderFormattedText from './utils/renderFormattedText'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'
import {
    preProcessTextForSpeechRecognition,
    createTokensFromSnippet,
    SpeakAlternativeToken,
    associateSpeechWithAlternativeToken,
    createSnippetTracker,
    associateSpeechWithSnippetTracker,
    SpeechEvaluationResultType,
    getPolishHomophoneStrings,
    POLISH_LANGUAGE_ID,
} from './speech/speechScoringLogic'

const ScenarioAudioPlayType = {
    DontPlay: 0,
    PlayBefore: 1,
    PlayAfter: 2,
}

const InteractiveElementType = {
    Image: 7,
    Definition: 10,
    MentorAnimation: 15,
}

const MAX_RECORDING_SECONDS = 10
const JACCARD_THRESHOLD = 0.65
const RESULT_FLASH_DURATION_MS = 2000

const getElementType = (to) =>
    to.scenarioInteractiveElementType ?? to.interactiveElementType ?? 0

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        padding: '24px 16px',
        boxSizing: 'border-box',
        '@media (min-width: 600px) and (orientation: landscape)': {
            padding: '12px 16px',
        },
        '@media (max-width: 599px)': {
            padding: '12px 8px',
        },
    },
    cardArea: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        gap: 20,
        overflow: 'auto',
        minHeight: 0,
        '@media (min-width: 600px) and (orientation: landscape)': {
            gap: 12,
        },
        '@media (max-width: 599px)': {
            gap: 14,
        },
    },
    card: {
        width: '100%',
        maxWidth: 400,
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        transition: 'box-shadow 400ms ease',
        flexShrink: 1,
        minHeight: 0,
        [theme.breakpoints.up('sm')]: {
            maxWidth: 500,
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: 600,
        },
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            borderRadius: 14,
        },
    },
    cardSuccess: {
        boxShadow: '0px 0px 0px 3px rgba(99, 194, 118, 0.5), 0px 8px 32px rgba(99, 194, 118, 0.3)',
    },
    cardFailed: {
        boxShadow: '0px 0px 0px 3px rgba(255, 102, 110, 0.35), 0px 8px 32px rgba(255, 102, 110, 0.2)',
    },
    image: {
        display: 'block',
        maxWidth: 360,
        width: '100%',
        borderRadius: 12,
        objectFit: 'contain',
        '@media (min-width: 600px) and (orientation: landscape)': {
            maxWidth: '60%',
            maxHeight: '25vh',
        },
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            maxHeight: '30vh',
            objectFit: 'contain',
        },
    },
    contentSection: {
        padding: '24px 24px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        [theme.breakpoints.up('sm')]: {
            padding: '28px 32px 24px',
        },
        '@media (min-width: 600px) and (orientation: landscape)': {
            padding: '16px 24px 12px',
        },
    },

    // Word text with result flash
    wordWrapper: {
        position: 'relative',
        padding: '8px 20px',
        borderRadius: 12,
        transition: 'background-color 400ms ease, transform 300ms ease',
    },
    wordWrapperSuccess: {
        backgroundColor: 'rgba(99, 194, 118, 0.15)',
        animation: '$popIn 400ms ease',
    },
    wordWrapperFailed: {
        backgroundColor: 'rgba(255, 102, 110, 0.12)',
        animation: '$popIn 400ms ease',
    },
    wordText: {
        fontFamily: 'Rubik',
        fontSize: 24,
        fontWeight: 600,
        color: CapeeshColors.DarkText,
        textAlign: 'center',
        transition: 'color 300ms ease',
        [theme.breakpoints.up('sm')]: {
            fontSize: 28,
        },
    },
    wordTextSuccess: {
        color: CapeeshColors.Green,
    },
    wordTextFailed: {
        color: CapeeshColors.PinkBright,
    },
    explanationText: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 400,
        lineHeight: '160%',
        color: 'rgba(0, 0, 0, 0.55)',
        textAlign: 'center',
    },
    audioRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 4,
    },
    listenButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: '50%',
        border: `2px solid ${CapeeshColors.BlueBright}`,
        backgroundColor: 'transparent',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            backgroundColor: 'rgba(90, 180, 241, 0.08)',
        },
    },
    listenIcon: {
        fontSize: 22,
        color: CapeeshColors.BlueBright,
    },
    listenIconPlaying: {
        animation: '$pulse 1.5s ease-in-out infinite',
    },

    // "Practice my pronunciation" button
    practiceButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        width: '100%',
        maxWidth: 400,
        padding: '16px 24px',
        borderRadius: 28,
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'Rubik',
        fontSize: 18,
        fontWeight: 600,
        transition: 'all 200ms ease, transform 150ms ease',
        backgroundColor: CapeeshColors.BlueBright,
        color: '#FFFFFF',
        boxShadow: '0px 4px 16px rgba(90, 180, 241, 0.35)',
        flexShrink: 0,
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 6px 20px rgba(90, 180, 241, 0.45)',
        },
        [theme.breakpoints.up('sm')]: {
            maxWidth: 500,
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: 600,
        },
        '@media (min-width: 600px) and (orientation: landscape)': {
            padding: '12px 20px',
            fontSize: 16,
        },
        '@media (max-width: 599px)': {
            maxWidth: '100%',
        },
    },
    practiceButtonRecording: {
        backgroundColor: CapeeshColors.Pink,
        boxShadow: '0px 4px 16px rgba(255, 81, 89, 0.35)',
        animation: '$recPulse 2s ease-in-out infinite',
        '&:hover': {
            boxShadow: '0px 6px 20px rgba(255, 81, 89, 0.45)',
        },
    },
    practiceButtonTryAgain: {
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        color: '#FFFFFF',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.18)',
            boxShadow: 'none',
        },
    },
    practiceButtonSuccess: {
        backgroundColor: CapeeshColors.Green,
        boxShadow: '0px 4px 16px rgba(99, 194, 118, 0.35)',
        pointerEvents: 'none',
    },
    practiceIcon: {
        fontSize: 22,
    },

    // Recording indicator
    recordingInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        maxWidth: 400,
        [theme.breakpoints.up('sm')]: {
            maxWidth: 500,
        },
    },
    timerBar: {
        width: '100%',
        height: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        borderRadius: 2,
        overflow: 'hidden',
    },
    timerFill: {
        height: '100%',
        transition: 'width 100ms linear',
        borderRadius: 2,
    },
    recognizedText: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.45)',
        textAlign: 'center',
        minHeight: 18,
        fontStyle: 'italic',
    },
    errorText: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        color: CapeeshColors.PinkBright,
        textAlign: 'center',
    },

    '@keyframes pulse': {
        '0%, 100%': { transform: 'scale(1)', opacity: 1 },
        '50%': { transform: 'scale(1.15)', opacity: 0.7 },
    },
    '@keyframes popIn': {
        '0%': { transform: 'scale(0.95)', opacity: 0.5 },
        '50%': { transform: 'scale(1.04)' },
        '100%': { transform: 'scale(1)', opacity: 1 },
    },
    '@keyframes recPulse': {
        '0%, 100%': { boxShadow: '0px 4px 16px rgba(255, 81, 89, 0.35)' },
        '50%': { boxShadow: '0px 4px 28px rgba(255, 81, 89, 0.6)' },
    },

    bottomArea: {
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0,
        width: '100%',
        padding: '20px 0 20px',
        boxSizing: 'border-box',
        '@media (min-width: 600px) and (orientation: landscape)': {
            padding: '12px 0 20px',
        },
        '@media (max-width: 599px)': {
            padding: '12px 16px 20px',
        },
    },
    continueButton: {
        padding: '16px 48px',
        borderRadius: 28,
        border: 'none',
        backgroundColor: '#FFFFFF',
        color: CapeeshColors.Green,
        fontFamily: 'Rubik',
        fontSize: 18,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
        transition: 'transform 150ms ease, box-shadow 150ms ease',
        width: '100%',
        maxWidth: 400,
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.2)',
        },
        [theme.breakpoints.up('sm')]: {
            maxWidth: 500,
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: 600,
        },
        '@media (min-width: 600px) and (orientation: landscape)': {
            padding: '12px 48px',
            fontSize: 16,
        },
    },
}))

const TestDefinitionNode = ({ node, imageMap, audioMap, onNodeComplete, lessonDto, l2LanguageId }) => {
    const classes = useStyles()
    const intl = useIntl()
    const audioPlayer = useAudioPlayer()
    const speech = useAzureSpeechRecognition(l2LanguageId)

    // idle | recording | success | failed
    const [pronunciationState, setPronunciationState] = useState('idle')
    const [recognizedText, setRecognizedText] = useState('')
    const [recordingElapsed, setRecordingElapsed] = useState(0)
    const [micError, setMicError] = useState(null)
    // Tracks the flash animation key so repeated attempts re-trigger the animation
    const [flashKey, setFlashKey] = useState(0)

    const allRecognizedTextRef = useRef('')
    const recordingTimerRef = useRef(null)
    const recordingStartRef = useRef(null)

    const testOptions = node.testOptions || []
    const definitionOption = testOptions.find((to) => getElementType(to) === InteractiveElementType.Definition)
    const imageOption = testOptions.find((to) => getElementType(to) === InteractiveElementType.Image)

    const wordText = definitionOption?.text || ''
    const explanationText = definitionOption?.explanation || ''
    const audioPlayType = definitionOption?.audioPlayType ?? ScenarioAudioPlayType.DontPlay
    const wordAudioUrl = definitionOption?.textAudioUrl ? audioMap[definitionOption.textAudioUrl] : null
    const imageUrl = imageOption?.imageUrl ? imageMap[imageOption.imageUrl] : null
    const alternatives = definitionOption?.alternatives || []

    const evaluationTargets = useMemo(() => {
        const snippetTrackers = []
        const alternativeTokens = []
        const speechEvaluationSnippets = lessonDto?.speechEvaluationSnippets || []
        const isPolish = l2LanguageId === POLISH_LANGUAGE_ID

        for (const alt of alternatives) {
            if (!alt.text) continue
            const snippet = speechEvaluationSnippets.find(
                (s) => s.snippetId === (alt.textSnippetId || '')
            )
            if (snippet) {
                snippetTrackers.push(createSnippetTracker(snippet, -1))
            } else {
                const trimmedText = preProcessTextForSpeechRecognition(alt.text)
                if (trimmedText) {
                    alternativeTokens.push(new SpeakAlternativeToken(trimmedText, -1))
                    if (isPolish) {
                        for (const variant of getPolishHomophoneStrings(alt.text)) {
                            const processed = preProcessTextForSpeechRecognition(variant)
                            if (processed && processed !== trimmedText) {
                                alternativeTokens.push(new SpeakAlternativeToken(processed, -1))
                            }
                        }
                    }
                }
            }
        }
        return { snippetTrackers, alternativeTokens }
    }, [alternatives, lessonDto?.speechEvaluationSnippets, l2LanguageId])

    useEffect(() => {
        if (audioPlayType === ScenarioAudioPlayType.PlayBefore && wordAudioUrl) {
            audioPlayer.playAudio(wordAudioUrl)
        }
        return () => {
            audioPlayer.stopAudio()
            speech.stopRecognition()
            if (recordingTimerRef.current) clearInterval(recordingTimerRef.current)
        }
    }, [])

    const handlePlayAudio = useCallback(() => {
        if (wordAudioUrl) {
            audioPlayer.stopAudio()
            audioPlayer.playAudio(wordAudioUrl)
        }
    }, [wordAudioUrl, audioPlayer])

    const evaluateSpeech = useCallback(
        (speechText, isFinal) => {
            const { snippetTrackers, alternativeTokens } = evaluationTargets

            let fullText
            if (isFinal) {
                if (allRecognizedTextRef.current) {
                    allRecognizedTextRef.current += ' ' + speechText
                } else {
                    allRecognizedTextRef.current = speechText
                }
                fullText = allRecognizedTextRef.current
            } else {
                fullText = allRecognizedTextRef.current
                    ? `${allRecognizedTextRef.current} ${speechText}`
                    : speechText
            }

            if (process.env.NODE_ENV !== 'production') {
                console.log('[TestDefinitionNode] evaluateSpeech:', { speechText, isFinal, fullText, snippetCount: snippetTrackers.length, altTokenCount: alternativeTokens.length })
            }

            if (snippetTrackers.length > 0) {
                const tokens = createTokensFromSnippet(fullText, l2LanguageId)
                if (tokens.length > 0) {
                    for (const tracker of snippetTrackers) {
                        const result = associateSpeechWithSnippetTracker(tokens, tracker)
                        if (result.result !== SpeechEvaluationResultType.NotPassed) {
                            if (process.env.NODE_ENV !== 'production') {
                                console.log('[TestDefinitionNode] snippet match — passed')
                            }
                            return true
                        }
                    }
                }
            }

            if (alternativeTokens.length > 0) {
                const words = fullText.split(/\s+/).filter(Boolean)
                if (words.length > 0) {
                    for (const token of alternativeTokens) {
                        const result = associateSpeechWithAlternativeToken(
                            words, token, JACCARD_THRESHOLD, true
                        )
                        if (result.result !== SpeechEvaluationResultType.NotPassed) {
                            if (process.env.NODE_ENV !== 'production') {
                                console.log('[TestDefinitionNode] alternativeToken match — passed')
                            }
                            return true
                        }
                    }
                }
            }

            const finalResult = isFinal ? false : null
            if (process.env.NODE_ENV !== 'production') {
                console.log('[TestDefinitionNode] evaluateSpeech result:', finalResult)
            }
            return finalResult
        },
        [evaluationTargets, l2LanguageId]
    )

    const handlePronunciationFinished = useCallback(
        (passed) => {
            speech.stopRecognition()
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current)
                recordingTimerRef.current = null
            }
            setPronunciationState(passed ? 'success' : 'failed')
            setFlashKey((k) => k + 1)
            allRecognizedTextRef.current = ''
        },
        [speech]
    )

    const handleStartRecording = useCallback(async () => {
        setMicError(null)
        setRecognizedText('')
        allRecognizedTextRef.current = ''
        setPronunciationState('recording')
        setRecordingElapsed(0)
        audioPlayer.stopAudio()

        recordingStartRef.current = Date.now()
        recordingTimerRef.current = setInterval(() => {
            const elapsed = (Date.now() - recordingStartRef.current) / 1000
            setRecordingElapsed(elapsed)
            if (elapsed >= MAX_RECORDING_SECONDS) {
                handlePronunciationFinished(false)
            }
        }, 100)

        const started = await speech.startRecognition({
            onRecognizing: (text) => {
                const processed = preProcessTextForSpeechRecognition(text)
                setRecognizedText(processed)
                const result = evaluateSpeech(processed, false)
                if (result === true) handlePronunciationFinished(true)
            },
            onRecognized: (text) => {
                const processed = preProcessTextForSpeechRecognition(text)
                setRecognizedText(processed)
                const result = evaluateSpeech(processed, true)
                if (result === true) handlePronunciationFinished(true)
                else if (result === false) handlePronunciationFinished(false)
            },
            onError: (errMsg) => {
                setMicError(errMsg)
                handlePronunciationFinished(false)
            },
        })

        if (!started) {
            setPronunciationState('idle')
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current)
                recordingTimerRef.current = null
            }
            setMicError(intl.formatMessage(playerMessages.micPermissionError))
        }
    }, [speech, audioPlayer, evaluateSpeech, handlePronunciationFinished])

    const handlePracticeClick = useCallback(() => {
        if (pronunciationState === 'recording') {
            speech.stopRecognition()
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current)
                recordingTimerRef.current = null
            }
            setPronunciationState('failed')
            setFlashKey((k) => k + 1)
            allRecognizedTextRef.current = ''
        } else {
            handleStartRecording()
        }
    }, [pronunciationState, handleStartRecording, speech])

    const handleContinue = useCallback(() => {
        audioPlayer.stopAudio()
        speech.stopRecognition()
        onNodeComplete({
            nodeId: node.nodeId,
            chosenAlternativeIndex: 0,
        })
    }, [node.nodeId, onNodeComplete, audioPlayer, speech])

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Enter') handleContinue()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [handleContinue])

    // Derived UI state
    const timerPct = Math.min(100, (recordingElapsed / MAX_RECORDING_SECONDS) * 100)
    const timerColor = timerPct > 75 ? CapeeshColors.PinkBright : CapeeshColors.BlueBright
    const isSuccess = pronunciationState === 'success'
    const isFailed = pronunciationState === 'failed'
    const isRecording = pronunciationState === 'recording'

    const cardClass = [
        classes.card,
        isSuccess ? classes.cardSuccess : '',
        isFailed ? classes.cardFailed : '',
    ].filter(Boolean).join(' ')

    const wordWrapperClass = [
        classes.wordWrapper,
        isSuccess ? classes.wordWrapperSuccess : '',
        isFailed ? classes.wordWrapperFailed : '',
    ].filter(Boolean).join(' ')

    const wordTextClass = [
        classes.wordText,
        isSuccess ? classes.wordTextSuccess : '',
        isFailed ? classes.wordTextFailed : '',
    ].filter(Boolean).join(' ')

    const getPracticeButtonClass = () => {
        if (isRecording) return `${classes.practiceButton} ${classes.practiceButtonRecording}`
        if (isSuccess) return `${classes.practiceButton} ${classes.practiceButtonSuccess}`
        if (isFailed) return `${classes.practiceButton} ${classes.practiceButtonTryAgain}`
        return classes.practiceButton
    }

    const getPracticeLabel = () => {
        if (isRecording) return intl.formatMessage(playerMessages.stopRecording)
        if (isSuccess) return intl.formatMessage(playerMessages.correctFeedback)
        if (isFailed) return intl.formatMessage(playerMessages.tryAgainFeedback)
        return intl.formatMessage(playerMessages.practicePronunciation)
    }

    const getPracticeIcon = () => {
        if (isRecording) return <StopIcon className={classes.practiceIcon} />
        return <MicIcon className={classes.practiceIcon} />
    }

    return (
        <div className={classes.container}>
            <div className={classes.cardArea}>
                {imageUrl && (
                    <img src={imageUrl} alt="" className={classes.image} />
                )}

                {/* Word card */}
                <div className={cardClass}>
                    <div className={classes.contentSection}>
                        {/* Word with result flash highlight */}
                        <div key={flashKey} className={wordWrapperClass}>
                            <Typography className={wordTextClass}>
                                {renderFormattedText(wordText)}
                            </Typography>
                        </div>

                        {explanationText && (
                            <Typography className={classes.explanationText}>
                                {renderFormattedText(explanationText)}
                            </Typography>
                        )}

                        {wordAudioUrl && (
                            <div className={classes.audioRow}>
                                <button className={classes.listenButton} onClick={handlePlayAudio}>
                                    <VolumeUpIcon
                                        className={`${classes.listenIcon} ${
                                            audioPlayer.isPlaying ? classes.listenIconPlaying : ''
                                        }`}
                                    />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Practice pronunciation button */}
                <button
                    className={getPracticeButtonClass()}
                    onClick={handlePracticeClick}
                    disabled={isSuccess}
                >
                    {getPracticeIcon()}
                    {getPracticeLabel()}
                </button>

                {/* Recording progress & feedback */}
                {isRecording && (
                    <div className={classes.recordingInfo}>
                        <div className={classes.timerBar}>
                            <div
                                className={classes.timerFill}
                                style={{ width: `${timerPct}%`, backgroundColor: timerColor }}
                            />
                        </div>
                        {recognizedText && (
                            <Typography className={classes.recognizedText}>
                                &ldquo;{recognizedText}&rdquo;
                            </Typography>
                        )}
                    </div>
                )}

                {micError && (
                    <Typography className={classes.errorText}>{micError}</Typography>
                )}
                {speech.error && !micError && (
                    <Typography className={classes.errorText}>{speech.error}</Typography>
                )}
            </div>

            <div className={classes.bottomArea}>
                <button className={classes.continueButton} onClick={handleContinue}>
                    <FormattedMessage {...playerMessages.continueButton} />
                </button>
            </div>
        </div>
    )
}

export default TestDefinitionNode
